import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { BrokerModuleService } from 'src/app/modules/broker/service/broker-module.service';
import { ReportsModuleService } from 'src/app/modules/broker/report-new/service/reports-module.service';
import { SharedModuleService } from 'src/app/core/services/shared-module.service';
import { RoleId } from 'src/app/core/common-enums/enum';
import { BrokerAdminRmClient, RMData } from 'src/app/core/models/broker-admin';
import { ClientReportResponse } from 'src/app/core/models/transaction.model';
import {
  AdvisorData,
  ClientListResult,
  DropdownOption,
  RmListResult
} from '../invite-update-sidebar.models';

@Injectable({ providedIn: 'root' })
export class InviteUpdateSidebarDataService {
  private readonly brokerService = inject(BrokerModuleService);
  private readonly reportService = inject(ReportsModuleService);
  private readonly sharedService = inject(SharedModuleService);

  /**
   * Loads and transforms the RM list for assignment/reassignment dropdowns.
   * Excludes the currently assigned RM and returns dropdown options plus the raw list.
   */
  getRmList(
    organizationId: number,
    excludeUserId: string
  ): Observable<RmListResult> {
    return this.brokerService.getAssignReassignRm(organizationId).pipe(
      map((data) => {
        const list: BrokerAdminRmClient[] = data ?? [];
        const userMemberRole = this.toCamelCase(
          list.find((m) => m.memberUserId === excludeUserId)?.memberRole ??
            'RM / Broker'
        );
        const rawList = list.filter((rm) => rm.memberUserId !== excludeUserId);
        const options: DropdownOption[] = rawList.map((rm) => ({
          label:
            `${rm.memberFirstName || ''} ${rm.memberMiddleName || ''} ${rm.memberLastName || ''}`.trim(),
          value: rm.memberUserId ?? '',
          data: rm
        }));
        return { options, userMemberRole, rawList };
      }),
      catchError(() =>
        of({ options: [], userMemberRole: 'RM / Broker', rawList: [] })
      )
    );
  }

  /**
   * Loads and transforms the client list for assignment/reassignment dropdowns.
   * Filters out clients assigned to the given RM and normalises name fields.
   */
  getClientList(
    userId: string,
    roleId: number,
    excludeRmUserId: string
  ): Observable<ClientListResult> {
    return this.reportService.clientName(userId, roleId).pipe(
      map((data) => {
        const transformed = (data ?? []).map((client: any) => ({
          ...client,
          name: [
            client.clientFirstName || '',
            client.clientMiddleName || '',
            client.clientLastName || ''
          ]
            .filter((p: string) => p.trim())
            .join(' ')
            .trim(),
          pan: client.clientPan || '',
          userId: client.clientUserId || client.userId,
          arn: client.arn || ''
        }));
        const rawList = transformed.filter(
          (item: any) => item.assignedRMUserId !== excludeRmUserId
        );
        const options: DropdownOption[] = rawList.map(
          (client: ClientReportResponse) => ({
            label: `${client.name || ''} - ${client.pan || ''}`.trim(),
            value: client.userId || '',
            data: client
          })
        );
        return { options, rawList };
      }),
      catchError(() => of({ options: [], rawList: [] }))
    );
  }

  /**
   * Fetches client RM or Advisor info and normalises it to the RMData shape.
   */
  getClientRmInfo(
    clientUserId: string,
    roleId: number
  ): Observable<RMData | null> {
    const apiCall =
      roleId === RoleId.Advisor
        ? this.brokerService.getClientAdvisorInfo(clientUserId)
        : this.brokerService.getClientRmInfo(clientUserId);

    return apiCall.pipe(
      map((data: RMData | AdvisorData | null) => {
        if (!data) {
          return null;
        }
        if (roleId === RoleId.Advisor && 'advisorUserId' in data) {
          const a = data as AdvisorData;
          return {
            rmUserId: a.advisorUserId || '',
            rmFirstName: a.firstName || '',
            middleName: a.middleName || '',
            lastName: a.lastName || '',
            virtualRMId: ''
          } as RMData;
        }
        return data as RMData;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Retrieves the dependant list for a given client.
   */
  getDependants(clientId: string) {
    return this.sharedService.getDepandantList(clientId);
  }

  /**
   * Title-cases each word in the given text.
   */
  toCamelCase(text: string): string {
    return text
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }
}
